import { useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

// Video Section Component for DeployPro
interface VideoData {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  thumbnail?: string;
}

interface VideoSectionProps {
  onContactClick?: () => void;
  onWatchVideo?: (videoId: string) => void;
}

export function VideoSection({ onContactClick, onWatchVideo }: VideoSectionProps) {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState<{ [key: string]: boolean }>({
    video1: true,
    video2: true
  });
  const [isMuted, setIsMuted] = useState<{ [key: string]: boolean }>({
    video1: false,
    video2: true
  });

  const videos: VideoData[] = [
    {
      id: 'video1',
      title: t('video.video1_title'),
      description: t('video.video1_description'),
      videoUrl: '/videos/invideo-ai-1080-Bust-Pha-Kinh-Doanh-Deploy-App-Sieu-Toc-2025-10-04.mp4'
    },
    {
      id: 'video2', 
      title: t('video.video2_title'),
      description: t('video.video2_description'),
      videoUrl: '/videos/invideo-ai-1080-Trien-khai-App-Web-Sieu-Toc-Gia-Soc-2025-10-04.mp4'
    }
  ];

  // Ensure autoplay works when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      const videoIds = ['video1', 'video2'];
      videoIds.forEach((videoId) => {
        const videoElement = document.getElementById(`video-${videoId}`) as HTMLVideoElement;
        if (videoElement) {
          videoElement.muted = true;
          videoElement.play().catch(console.error);
        }
      });
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const togglePlay = (videoId: string) => {
    const video = document.getElementById(`video-${videoId}`) as HTMLVideoElement;
    if (video) {
      if (isPlaying[videoId]) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(prev => ({ ...prev, [videoId]: !prev[videoId] }));
    }
  };

  const toggleMute = (videoId: string) => {
    const video = document.getElementById(`video-${videoId}`) as HTMLVideoElement;
    if (video) {
      const newMutedState = !isMuted[videoId];
      video.muted = newMutedState;
      setIsMuted(prev => ({ ...prev, [videoId]: newMutedState }));
    }
  };

  const openFullscreen = (videoId: string) => {
    const video = document.getElementById(`video-${videoId}`) as HTMLVideoElement;
    if (video && video.requestFullscreen) {
      video.requestFullscreen();
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            {t('video.section_title')}
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {t('video.section_subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {videos.map((video) => (
            <div key={video.id} className="video-card bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col">
              {/* Video Container */}
              <div className="relative bg-black aspect-video">
                <video
                  id={`video-${video.id}`}
                  className="w-full h-full object-cover"
                  onPlay={() => setIsPlaying(prev => ({ ...prev, [video.id]: true }))}
                  onPause={() => setIsPlaying(prev => ({ ...prev, [video.id]: false }))}
                  onEnded={() => setIsPlaying(prev => ({ ...prev, [video.id]: false }))}
                  controls={false}
                  preload="metadata"
                  playsInline
                  autoPlay
                  muted={video.id !== 'video1'}
                  loop
                >
                  <source src={video.videoUrl} type="video/mp4" />
                  Trình duyệt của bạn không hỗ trợ video.
                </video>

                {/* Custom Controls Overlay - Desktop */}
                <div className="hidden md:flex absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity duration-300 items-center justify-center">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => togglePlay(video.id)}
                      className="video-control-btn video-play-btn"
                    >
                      {isPlaying[video.id] ? (
                        <Pause className="w-8 h-8 text-slate-800" />
                      ) : (
                        <Play className="w-8 h-8 text-slate-800 ml-1" />
                      )}
                    </button>
                    
                    <button
                      onClick={() => toggleMute(video.id)}
                      className="video-control-btn"
                    >
                      {isMuted[video.id] ? (
                        <VolumeX className="w-5 h-5 text-slate-800" />
                      ) : (
                        <Volume2 className="w-5 h-5 text-slate-800" />
                      )}
                    </button>

                    <button
                      onClick={() => openFullscreen(video.id)}
                      className="video-control-btn"
                    >
                      <Maximize className="w-5 h-5 text-slate-800" />
                    </button>
                  </div>
                </div>

                {/* Play Button for Mobile */}
                {!isPlaying[video.id] && (
                  <div className="md:hidden absolute inset-0 flex items-center justify-center">
                    <button
                      onClick={() => togglePlay(video.id)}
                      className="video-control-btn video-play-btn shadow-2xl"
                    >
                      <Play className="w-10 h-10 text-slate-800 ml-1" />
                    </button>
                  </div>
                )}
              </div>

              {/* Video Info */}
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-slate-900 mb-3 line-clamp-2">
                  {video.title}
                </h3>
                <p className="text-slate-600 leading-relaxed line-clamp-3 mb-4 flex-grow">
                  {video.description}
                </p>
                
                {/* Watch Full Video Button - Fixed position at bottom */}
                <div className="mt-auto">
                  <button
                    onClick={() => onWatchVideo?.(video.id === 'video1' ? 'deploy-breakthrough' : 'deploy-web-app')}
                    className="w-full mb-4 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors"
                  >
                    {t('video.watch_full')}
                  </button>
                  
                  {/* Video Stats */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="flex items-center space-x-4 text-sm text-slate-500">
                      <span>{t('video.demo_video')}</span>
                      <span>{t('video.mobile_web')}</span>
                    </div>
                    <button
                      onClick={() => togglePlay(video.id)}
                      className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors duration-300"
                    >
                      {isPlaying[video.id] ? t('video.pause') : t('video.watch_now')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('video.cta_title')}
            </h3>
            <p className="text-emerald-100 text-lg mb-6">
              {t('video.cta_subtitle')}
            </p>
            <button 
              onClick={onContactClick}
              className="px-8 py-3 bg-white hover:bg-slate-100 text-emerald-600 font-bold rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
            >
              {t('video.cta_button')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default VideoSection;